// Первым: подкладывает сцену холста до того, как остальные модули прочитают хранилище
import './dev/scene-boot'
import { render } from 'preact'
import { appStorage, createWebPlatform, initWebPlatform, provideState, pushUnknownToServer, retryPending } from './platform/web'
import { connectNativeStorage, createNativePlatform, isNative } from './platform/native'
import { claimFromUrl } from './api/auth'
import { Splash } from './ui/Splash'
import { watchInstall } from './platform/install'
import { kids } from './stores/children'
import { prepareKids, settleUnnamedSchedule } from './stores/kids-boot'
import { loadSound } from './platform/sound'
import { flush, watchOutbox } from './api/outbox'
import { pullKidsOnStart } from './api/sync'
import { runSelfCheck } from './stores/selfcheck'
import { KidStorage } from './stores/children'
import { retryOldDelete } from './api/migrate'
import { loadRegion } from './api/region'
import { announceState, init, program, refresh } from './state/store'
import { profile } from './state/store'
import { sceneFromUrl, sceneHoldsSplash } from './dev/scenes'
import { initLang, lang, langFromPath, sitePath } from './i18n'
import './ui/theme.css'
import './ui/app.css'
import './ui/cabinet.css'
import './ui/dropdown.css'
import './site/landing.css'

const root = document.getElementById('app')
if (root === null) throw new Error('Нет корневого элемента #app')

/**
 * Один адрес, два лица (как в макете `Design_2/site`).
 *
 * Ширина окна здесь ни при чём: узкий экран говорит о размере окна, а не о намерении.
 * Человек, впервые открывший ссылку с телефона, хочет понять, что это за приложение,
 * и должен увидеть презентацию — у неё для этого есть свои мобильные правила.
 * Приложение открывается по явному признаку: адрес /app, установка на домашний экран
 * или оболочка для магазина.
 */
function installed(): boolean {
  return (
    globalThis.matchMedia?.('(display-mode: standalone)').matches === true ||
    (globalThis.navigator as { standalone?: boolean }).standalone === true
  )
}

/**
 * Пользовались ли этим устройством раньше: есть аккаунт или заведён малыш.
 * Читаем прямо из хранилища, до подключения площадки, — на этом шаге решается,
 * что вообще рисовать.
 */
function ownDevice(): boolean {
  try {
    // Только через getItem/key: у холста сверки хранилище своё, в памяти,
    // и перебор свойств объекта по нему ничего не находит.
    const прямые = ['moonwake.session', 'moonwake.email', 'moonwake.kids', 'moonwake.child_name']
    if (прямые.some((key) => localStorage.getItem(key) !== null)) return true
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key !== null && /^moonwake\.kid\.[^.]+\.child_name$/.test(key)) return true
    }
    return false
  } catch {
    // Приватный режим без хранилища — считаем, что человек здесь впервые
    return false
  }
}

/**
 * Приложение или презентация.
 *
 * Адреса `/` и `/app` — не две главные. Презентация рассказывает о Moonwake и ведёт
 * в регистрацию, приложение — то, чем пользуются каждый день. Человек с улицы,
 * открывший `/app`, видел приветствие приложения без всякого повода: страница из
 * ниоткуда. Поэтому `/app` показывается только тем, кому он предназначен:
 * установленному приложению, оболочке для магазина, ссылке входа из письма и
 * устройству, на котором уже есть аккаунт или малыш. Остальным — презентация.
 */
function wantsApp(): boolean {
  // Оболочка для магазина: там презентации быть не может по определению.
  if (isNative()) return true
  // Установлен на домашний экран: запускается как приложение, а не как страница.
  if (installed()) return true
  // Сцена холста сверки: она и открывается ради экранов приложения (только у нас).
  if (sceneFromUrl() !== null) return true
  if (!globalThis.location.pathname.startsWith('/app')) return false
  // Ссылка входа из письма: ключ едет в хеше, и открыть её надо именно приложением.
  if (/^#login=/.test(globalThis.location.hash)) return true
  return ownDevice()
}

/**
 * Приложение не масштабируется двумя пальцами: это экран с кнопками, а не страница для
 * чтения (жалоба 28.09.2026). Презентацию и политику не трогаем — их читают и приближают.
 */
function lockZoom(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  meta?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover')
}

/**
 * Сайт живёт по адресам `/` (английский), `/ru`, `/zh`, `/es` (решение 8). Пришли на корень,
 * а язык браузера не английский — один раз за сессию уводим на свой адрес; человек может
 * вернуться на `/` и остаться. Явный выбор в хранилище сильнее браузера (раздел 4.1).
 */
function положитьСайтНаСвойАдрес(): void {
  const path = globalThis.location.pathname
  if (path !== '/' || langFromPath(path) !== null) return
  if (lang.value === 'en') return
  try {
    if (sessionStorage.getItem('moonwake.site_lang_redirect') !== null) return
    sessionStorage.setItem('moonwake.site_lang_redirect', '1')
  } catch {
    return
  }
  history.replaceState(null, '', sitePath(lang.value) + globalThis.location.search + globalThis.location.hash)
}

async function start(container: HTMLElement): Promise<void> {
  // Язык раньше всего: заставка и презентация тоже говорят на нём
  await initLang()
  // Экран загрузки рисуется первым и живёт, пока читается хранилище и заводится площадка.
  // Раньше он был мёртвым кодом: приложение показывало его уже после того, как всё готово,
  // то есть никогда, а человек видел пустой тёмный прямоугольник.
  if (wantsApp()) {
    lockZoom()
    render(<Splash />, container)
  }
  // Холст сверки: сцена «Загрузка» показывает этот экран и больше ничего
  if (sceneHoldsSplash()) return

  // Предложение установки браузер показывает один раз; перехватываем, чтобы вернуть к нему кнопкой.
  watchInstall()
  loadSound()
  // В приложении из магазина данные лежат в настройках Android, а не в localStorage.
  // Подключаем их до чтения малышей: иначе список ляжет в одно хранилище, данные — в другое,
  // и второй малыш затрёт первого (проверено на эмуляторе 23.09.2026).
  if (isNative()) await connectNativeStorage()
  // Регион хранения данных — до первого обращения к серверу: от него зависит адрес API
  await loadRegion()
  // Раскладка по малышам: переезд со старой версии и выбор текущего — до чтения данных
  await prepareKids()

  if (!wantsApp()) {
    // Пришли на /app без повода — это презентация, и адрес должен быть её.
    if (globalThis.location.pathname.startsWith('/app')) history.replaceState(null, '', sitePath(lang.value))
    положитьСайтНаСвойАдрес()
    // Хранилище нужно и презентации: окно регистрации сохраняет профиль прямо там.
    await init(createWebPlatform())
    const { Landing } = await import('./site/Landing')
    render(<Landing />, container)
    return
  }

  // Пришли по ссылке из письма: забираем вход до первой отрисовки, чтобы приложение
  // сразу открылось от имени человека, а не показало ему вход ещё раз.
  await claimFromUrl()

  // В оболочке для магазина будильники ставит Android, поэтому ни сервера, ни push не нужно.
  if (isNative()) {
    await init(await createNativePlatform())
    const { App } = await import('./app')
    render(<App />, container)
    return
  }

  await initWebPlatform()
  await init(createWebPlatform())
  // Вошли, а малышей на устройстве нет — это новый телефон. Забираем их со счёта до
  // первой отрисовки: иначе человек увидит приветствие и заполнит анкету заново,
  // заведя на счёте второго такого же малыша. Ждём недолго: сеть бывает медленной.
  await Promise.race([pullKidsOnStart(appStorage()), new Promise((done) => setTimeout(done, 6000))])
  // Самопроверка устройства: тихий разлад виден только отсюда — сервер его не замечает,
  // потому что обмен при этом идёт как ни в чём не бывало (28.10.2026)
  const хранилище = appStorage()
  void runSelfCheck(хранилище, (id) => (хранилище instanceof KidStorage ? хранилище.forKid(id) : хранилище)).catch(() => [])
  // Малыш приехал со счёта — перечитываем экраны: до этого сторы прочитали пустоту
  if (kids.value.length > 0 && profile.value === null) await refresh()
  // Намерения, которые не ушли без сети: досылаем и следим за её возвращением
  watchOutbox()
  void flush()
  // Перенос страны мог не довести удаление старого аккаунта (оборвалась сеть) — добиваем
  void retryOldDelete()
  // Площадке иногда нужно текущее состояние окна, а знает его стор
  provideState(announceState)
  const { App } = await import('./app')
  render(<App />, container)

  // Переезд на нескольких малышей мог оставить на сервере безымянное расписание:
  // его задания продолжали бы приходить вторым, точно таким же уведомлением.
  void settleUnnamedSchedule(program.value)

  // Уведомления включены на телефоне, а сервер о подписке не знает — отдаём её.
  // Такое бывает, когда человек включил уведомления и не трогал таймер: подписка
  // уезжает вместе с расписанием, а расписание без действия не отправлялось.
  void pushUnknownToServer().then((нужно) => {
    if (нужно) void announceState()
  })

  // Вернулись на вкладку или появилась сеть — досылаем расписание, если прошлая попытка не удалась.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') retryPending()
  })
  globalThis.addEventListener('online', retryPending)
}

void start(root)
